import { Groups2Outlined, BoltOutlined, TrendingUpOutlined } from "@mui/icons-material";
import { inject, observer } from "mobx-react";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton, useToast } from "randmarcomps";
import ResellersGrid from "../components/grids/ResellersGrid";
import SalesSummary from "../componentsCore/cards/SalesSummary";
import withManufacturer from "../contexts/WithManufacturer";
import Path from "../routes/Path";
import { RootStore } from "../stores/rootStore";
import withRoot from "../withRoot";

interface IProps {
  store?: RootStore;
  applicationId: string;
}

const Resellers: React.FC<IProps> = ({ store, applicationId }) => {
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const { resellerStore } = store!;

    const loadResellers = async () => {
      if (applicationId) {
        try {
          await resellerStore.getResellers(applicationId);
        } catch (error) {
          toast({
            variant: "destructive",
            title: "Error",
            description: "There was an error loading resellers.",
          });
        }
      }
      setLoading(false);
    };

    loadResellers();
  }, [applicationId, store, toast]);

  const { resellerStore, manufacturerStore } = store!;
  const { topResellers } = resellerStore;
  const { manufacturer } = manufacturerStore;

  const sortedResellers = useMemo(
    () =>
      (topResellers || [])
        .slice()
        .sort((b, a) => (a.salesData?.salesLast30Days || 0) - (b.salesData?.salesLast30Days || 0)),
    [topResellers]
  );

  const stats = useMemo(() => {
    const total = sortedResellers.length;
    const active = sortedResellers.filter((reseller) => (reseller.salesData?.salesLast30Days || 0) > 0).length;
    const onHold = sortedResellers.filter((reseller) => reseller.onHold === 1).length;
    const total30DaySales = sortedResellers.reduce((sum, reseller) => sum + (reseller.salesData?.salesLast30Days || 0), 0);
    const average30DaySales = total ? total30DaySales / total : 0;

    return {
      total,
      active,
      onHold,
      total30DaySales,
      average30DaySales,
      engagement: total ? (active / total) * 100 : 0,
    };
  }, [sortedResellers]);

  const formatCompactNumber = (value: number) => {
    if (!value) return "0";
    const useCompact = Math.abs(value) >= 1000;
    return new Intl.NumberFormat(undefined, {
      notation: useCompact ? "compact" : "standard",
      maximumFractionDigits: useCompact ? 1 : 0,
    }).format(value);
  };

  const highlightStats = useMemo(
    () => [
      {
        label: "Qualified Network",
        value: formatCompactNumber(stats.total),
        helper: "resellers ready to go",
        icon: Groups2Outlined,
      },
      {
        label: "Active (30 days)",
        value: formatCompactNumber(stats.active),
        helper: `${Math.round(stats.engagement || 0)}% engagement`,
        icon: BoltOutlined,
      },
      {
        label: "Avg 30-Day Sales",
        value: formatCompactNumber(Math.round(stats.average30DaySales)),
        helper: stats.total ? "per reseller" : "Awaiting orders",
        icon: TrendingUpOutlined,
      },
    ],
    [stats.active, stats.average30DaySales, stats.engagement, stats.total]
  );

  const spotlightReseller = sortedResellers[0];
  const supportingResellers = sortedResellers.slice(1, 4);

  const formatLocation = (reseller?: typeof spotlightReseller) => {
    if (!reseller) return "";
    return [reseller.city, reseller.province, reseller.country].filter(Boolean).join(", ");
  };

  if (loading) {
    return (
      <>
        <div className="my-5 w-full">
          <Skeleton className="h-32 w-full" />
        </div>
        <div className="w-full">
          <h1 className="text-2xl">Qualified Resellers</h1>
          <Skeleton className="mt-4 h-96 w-full" />
        </div>
      </>
    );
  }

  return (
    <div className="space-y-8 pb-10">
      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <div className="rounded-3xl bg-gradient-to-r from-indigo-600 via-purple-600 to-rose-500 p-6 text-white shadow-xl sm:p-8">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/80">Reseller network</p>
              <h1 className="mt-3 text-3xl font-semibold leading-tight sm:text-4xl">Build momentum with your qualified partners</h1>
              <p className="mt-3 max-w-2xl text-base text-white/80">Track performance, prioritize outreach, and activate new opportunities with a single view of your reseller activity.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link
                to={Path.createOpportunity(applicationId)}
                className="inline-flex items-center justify-center rounded-full bg-white/20 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/30"
              >
                Create Opportunity
              </Link>
              <Link
                to={Path.opportunities(applicationId)}
                className="inline-flex items-center justify-center rounded-full border border-white/40 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                View Pipeline
              </Link>
            </div>
          </div>

          <dl className="mt-8 grid gap-4 sm:grid-cols-3">
            {highlightStats.map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="rounded-2xl bg-white/15 p-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15">
                      <Icon className="h-6 w-6" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-white/80">{stat.label}</p>
                      <p className="text-2xl font-semibold text-white">{stat.value}</p>
                      <p className="text-xs text-white/70">{stat.helper}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </dl>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sales pulse</p>
          <p className="text-sm text-slate-500">Manufacturer performance snapshot</p>
          <div className="mt-4">
            <SalesSummary salesData={manufacturer?.salesData} fiscalYearMonthStart={manufacturer?.fiscalYearMonthStart} />
          </div>
        </div>
      </section>

      {spotlightReseller && (
        <section className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Spotlight reseller</p>
            <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-2xl font-semibold text-slate-900">{spotlightReseller.publicName || spotlightReseller.name}</h2>
                <p className="text-sm text-slate-500">{formatLocation(spotlightReseller)}</p>
              </div>
              {spotlightReseller.resellerId && (
                <Link
                  to={Path.reseller(applicationId, spotlightReseller.resellerId)}
                  className="text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                >
                  View profile →
                </Link>
              )}
            </div>

            <dl className="mt-6 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">30-day sales</dt>
                <dd className="mt-2 text-2xl font-semibold text-slate-900">{formatCompactNumber(spotlightReseller.salesData?.salesLast30Days || 0)}</dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</dt>
                <dd className="mt-2 text-base font-semibold text-slate-900">{spotlightReseller.isQualified ? "Qualified" : "Review"}</dd>
              </div>
              <div className="rounded-2xl bg-slate-50 p-4">
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Qualifications</dt>
                <dd className="mt-2 text-base font-semibold text-slate-900">{spotlightReseller.qualifications?.length || 0}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Rising performers</p>
            <ul className="mt-4 space-y-4">
              {supportingResellers.length === 0 && <li className="text-sm text-slate-500">We will highlight more resellers as new activity comes in.</li>}
              {supportingResellers.map((reseller) => (
                <li key={reseller.resellerId || reseller.name} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-medium text-slate-900">{reseller.publicName || reseller.name}</p>
                    <p className="text-xs text-slate-500">{formatLocation(reseller)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">{formatCompactNumber(reseller.salesData?.salesLast30Days || 0)}</p>
                    <p className="text-xs text-slate-500">30-day sales</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-lg font-semibold text-slate-900">Qualified Resellers</p>
            <p className="text-sm text-slate-500">Sort, search, and take action on the partners that match your next opportunity.</p>
          </div>
          <div className="text-sm text-slate-500">
            {stats.onHold > 0 ? `${stats.onHold} on hold` : "All partners active"}
          </div>
        </div>
        <div className="mt-4">
          <ResellersGrid resellers={sortedResellers} defaultRowsPerPage={25} />
        </div>
      </section>
    </div>
  );
};

export default withRoot(withManufacturer(inject("store")(observer(Resellers))));
